// @flow
import { takeEvery, call, put, spawn } from 'redux-saga/effects';
import {
  PRODUCTS,
  receiveProducts,
  receiveMoreProducts,
  receiveSponsoredProducts,
  failedProductsRequest,
  receiveBestSellingProducts,
  receiveBestSellingProductsError,
  receiveSearchProducts,
} from 'pages/MyAccount/services/actions/productsActions';

import {
  getProducts,
  getFavoriteProducts,
  getSponsoredProducts,
  getBestSellingProducts,
  getProductsById,
} from 'pages/MyAccount/services/api/products';
import { getCartData } from 'pages/MyAccount/services/api/carts';

import { CATEGORY_ID } from 'pages/MyAccount/utils/categories';
import { isPaginatedSortBy } from '../../utils/sort';

const {
  REQUEST_PRODUCTS,
  REQUEST_BEST_SELLING_PRODUCTS,
  REQUEST_PRODUCTS_BY_ID,
  REQUEST_SEARCH_PRODUCTS,
} = PRODUCTS;

// watcher sagas
export function* watchFetchProducts() {
  yield takeEvery(REQUEST_PRODUCTS, fetchProducts);
  yield takeEvery(REQUEST_BEST_SELLING_PRODUCTS, fetchBestSellingProducts);
  yield takeEvery(REQUEST_PRODUCTS_BY_ID, fetchProductsById);
  yield takeEvery(REQUEST_SEARCH_PRODUCTS, fetchSearchProducts);
}

// worker sagas
export function* fetchProducts(action) {
  /* using try catch here so that if this worker SAGA fails, 
  we can display failed products ui. */
  const { offset, selectedCategoryId, selectedSort, selectedProductLink } = action.payload;
  try {
    yield call(
      fetchProductsByCategory,
      offset,
      selectedCategoryId,
      selectedSort,
      selectedProductLink,
    );
  } catch {
    yield put(failedProductsRequest());
  }
}

function* fetchProductsByCategory(
  offset,
  selectedCategoryId,
  selectedSort,
  selectedProductLink = null,
) {
  /* using spawn here so if fetchSponsoredProducts errors, 
  it does not stop fetchProductsByCategory worker from running */
  if (offset === 0) yield spawn(fetchSponsoredProducts);

  let data;
  if (selectedCategoryId === CATEGORY_ID.mycart) {
    data = yield call(fetchCartCategoryProducts);
  } else if (selectedCategoryId === CATEGORY_ID.myfavorites) {
    data = yield call(getFavoriteProducts, selectedSort);
  } else if (selectedCategoryId === CATEGORY_ID.sponsored) {
    data = {
      productIds: [],
      products: {},
      totalProductCount: 0,
      variants: {},
    };
  } else {
    data = yield call(
      getProducts,
      // we don't paginate best selling sort, so there's no offset in that case
      !isPaginatedSortBy(selectedSort) ? null : offset,
      selectedCategoryId,
      selectedSort,
      selectedProductLink,
    );
  }

  if (offset === 0) {
    yield put(receiveProducts(data));
  } else {
    yield put(receiveMoreProducts(data));
  }
}

function* fetchSponsoredProducts() {
  const sponsoredProductsResponse = yield call(getSponsoredProducts);
  yield put(receiveSponsoredProducts(sponsoredProductsResponse));
}

function* fetchCartCategoryProducts() {
  const cartData = yield call(getCartData);
  const { products, variants, cartDetails } = cartData;
  const { variantIds, variantQuantities } = cartDetails;
  // TODO: Resolve the fact that the cart endpoint potentially gives us variants not currently in cart.
  const variantIdsInCart = variantIds.filter((id) => variantQuantities[id] > 0);
  const productIds = sortCartProductIds(products, variantIdsInCart);
  return {
    products,
    variants,
    productIds,
    totalProductCount: productIds.length,
  };
}

function sortCartProductIds(products, cartVariantIds) {
  const productIds = [];
  // TODO: does this need to be a forEach?
  // eslint-disable-next-line guard-for-in
  for (const id in products) {
    productIds.push(id);
  }
  const sortedProductIds = productIds.sort((a, b) => {
    const productA = products[a];
    const productB = products[b];
    const indexOfFirstVariantInCartA = productA.variantIds.findIndex((el) =>
      cartVariantIds.includes(el),
    );
    const indexOfFirstVariantInCartB = productB.variantIds.findIndex((el) =>
      cartVariantIds.includes(el),
    );
    if (indexOfFirstVariantInCartA === indexOfFirstVariantInCartB) return 0;
    return indexOfFirstVariantInCartA < indexOfFirstVariantInCartB ? -1 : 1;
  });
  return sortedProductIds;
}

function* fetchBestSellingProducts(action) {
  yield spawn(fetchSponsoredProducts);
  const { selectedCategoryId, selectedProductLink, selectedSort, productRecAbKey } = action.payload;
  try {
    const response = yield call(
      getBestSellingProducts,
      selectedCategoryId,
      selectedProductLink,
      selectedSort,
      productRecAbKey,
    );
    yield put(receiveBestSellingProducts(response));
  } catch (err) {
    yield put(receiveBestSellingProductsError(err));
  }
}

function* fetchProductsById(action) {
  const { productIds } = action;
  const response = yield call(getProductsById, productIds);
  yield put(receiveMoreProducts(response));
}

function* fetchSearchProducts(action) {
  const { productIds } = action;
  const response = yield call(getProductsById, productIds);
  yield put(receiveSearchProducts(response));
}
